/**
 * Shop Integrations Section Component - Minimal Design
 * Settings sayfasında shop integrations yönetimi için component
 */

import React, { useState } from 'react';
import { useShopIntegrations, useCreateShopIntegration, useUpdateShopIntegration, useDeleteShopIntegration } from '@/hooks/useShopIntegrations';
import { useOrganizations } from '@/hooks/useOrganizations';
import api from '@/lib/api';
import { Card, CardContent, CardHeader } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Badge } from '@/components/ui/badge';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger, DialogFooter } from '@/components/ui/dialog';
import { Store, Plus, Edit, Trash2, ShoppingBag, Globe, Key } from 'lucide-react';
import { cn } from '@/lib/utils';
import { Skeleton } from './ui/skeleton';
import { useTranslation } from 'react-i18next';

const ShopIntegrationsSection = () => {
  const { t } = useTranslation();
  const [selectedOrganizationId, setSelectedOrganizationId] = useState<string>('');
  const [isCreateModalOpen, setIsCreateModalOpen] = useState(false);
  const [isEditModalOpen, setIsEditModalOpen] = useState(false);
  const [editingIntegration, setEditingIntegration] = useState<any>(null);
  const [formData, setFormData] = useState({
    platformType: 'shopify',
    platformName: '',
    connectionConfig: {
      shop_url: '',
      access_token: '',
      api_version: '2023-10',
      jtl_url: '',
      api_key: ''
    }
  });

  // Queries & Mutations
  const { data: organizationsData, isLoading: isLoadingOrganizations, error: organizationsError } = useOrganizations();
  const createMutation = useCreateShopIntegration();
  const updateMutation = useUpdateShopIntegration();
  const deleteMutation = useDeleteShopIntegration();

  const organizations = organizationsData?.data && Array.isArray(organizationsData.data) ? organizationsData.data : [];

  // Tüm organizasyonların integrations'larını al
  const [allIntegrations, setAllIntegrations] = useState<any[]>([]);
  const [isLoadingAllIntegrations, setIsLoadingAllIntegrations] = useState(false);

  // Tüm organizasyonların integrations'larını yükle
  React.useEffect(() => {
    const loadAllIntegrations = async () => {
      if (!Array.isArray(organizations) || organizations.length === 0) {
        return;
      }

      setIsLoadingAllIntegrations(true);
      const allInts: any[] = [];

      for (const org of organizations) {
        try {
          const response = await api.get(`/shop-integrations/${org.id}`);

          if (response.data.success && response.data.data && response.data.data.integrations) {
            const integrationsWithOrg = response.data.data.integrations.map((integration: any) => ({
              ...integration,
              organizationName: org.name
            }));
            allInts.push(...integrationsWithOrg);
          }
        } catch (error) {
          console.error(`Error loading integrations for ${org.name}:`, error);
        }
      }

      setAllIntegrations(allInts);
      setIsLoadingAllIntegrations(false);
    };

    loadAllIntegrations();
  }, [organizations]);

  const selectedOrganization = Array.isArray(organizations) ? organizations.find(org => org.id === selectedOrganizationId) : null;
  const selectedOrganizationName = selectedOrganization?.name || 'Organisation wählen';

  // Handlers
  const handleCreateIntegration = async () => {
    if (!selectedOrganizationId) return;

    try {
      await createMutation.mutateAsync({
        organizationId: selectedOrganizationId,
        data: {
          platformType: formData.platformType as any,
          platformName: formData.platformName,
          connectionConfig: formData.connectionConfig,
          isActive: true,
          isDefault: false
        }
      });
      setIsCreateModalOpen(false);
      setFormData({
        platformType: 'shopify',
        platformName: '',
        connectionConfig: {
          shop_url: '',
          access_token: '',
          api_version: '2023-10',
          jtl_url: '',
          api_key: ''
        }
      });
      window.location.reload();
    } catch (error) {
      console.error('Integration oluşturma hatası:', error);
    }
  };

  const handleEditIntegration = (integration: any) => {
    setEditingIntegration(integration);
    setFormData({
      platformType: integration.platform_type,
      platformName: integration.platform_name,
      connectionConfig: integration.connection_config || {
        shop_url: '',
        access_token: '',
        api_version: '2023-10',
        jtl_url: '',
        api_key: ''
      }
    });
    setIsEditModalOpen(true);
  };

  const handleUpdateIntegration = async () => {
    if (!editingIntegration) return;

    try {
      await updateMutation.mutateAsync({
        id: editingIntegration.id,
        data: {
          platformName: formData.platformName,
          connectionConfig: formData.connectionConfig,
          isActive: editingIntegration.is_active,
          isDefault: editingIntegration.is_default
        }
      });
      setIsEditModalOpen(false);
      setEditingIntegration(null);
      setFormData({
        platformType: 'shopify',
        platformName: '',
        connectionConfig: {
          shop_url: '',
          access_token: '',
          api_version: '2023-10',
          jtl_url: '',
          api_key: ''
        }
      });
      window.location.reload();
    } catch (error) {
      console.error('Integration güncelleme hatası:', error);
    }
  };

  const handleDeleteIntegration = async (integrationId: string) => {
    if (!confirm(t('common:x3.confirmDeleteIntegration', 'Diese Integration wirklich löschen?'))) return;

    try {
      await deleteMutation.mutateAsync(integrationId);
      window.location.reload();
    } catch (error) {
      console.error('Integration silme hatası:', error);
    }
  };

  if (isLoadingOrganizations) {
    return <div className="p-6 space-y-4">
      <Skeleton className="h-8 w-64" />
      <div className="space-y-2">
        <Skeleton className="h-12 w-full" />
        <Skeleton className="h-12 w-full" />
      </div>
    </div>;
  }

  return (
    <div className="space-y-4">
      {/* Main Card */}
      <Card className="border border-white/70 bg-white/70 backdrop-blur-xl ring-1 ring-black/5">
        <CardHeader className="flex flex-row items-center justify-between pb-2 border-b border-slate-100">
          <div className="space-y-1">
            <div className="flex flex-wrap items-center gap-2">
              <h3 className="text-base font-semibold text-slate-900">{t('common:x2.integrations', 'Integrationen')}</h3>
              <span className="text-xs text-slate-400 font-normal ml-2">
                ({selectedOrganizationId
                  ? `${allIntegrations.filter(i => i.organization_id === selectedOrganizationId).length} Integrationen`
                  : `${allIntegrations.length} Gesamt`
                })
              </span>
            </div>
            <p className="text-sm text-slate-500">
              {t('common:x2.connectEcommerce', 'Verbinden Sie Ihre E-Commerce-Plattformen und synchronisieren Sie Daten.')}
            </p>
          </div>
          
          <div className="flex items-center gap-3">
            <div className="flex items-center gap-2">
              <span className="text-xs font-medium text-slate-500">{t('common:x1.orgColon', 'Organisation:')}</span>
              <Select
                value={selectedOrganizationId || "all"}
                onValueChange={(value) => setSelectedOrganizationId(value === "all" ? "" : value)}
                disabled={isLoadingOrganizations}
              >
                <SelectTrigger className="h-8 w-[180px] border-slate-200 bg-slate-50/50 text-xs focus:ring-slate-200">
                  <SelectValue placeholder={t('ticket:ph.selectOrg', 'Organisation wählen')} />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="all" className="text-xs">{t('common:action.showAll', 'Alle anzeigen')}</SelectItem>
                  {organizations.map((org) => (
                    <SelectItem key={org.id} value={org.id} className="text-xs">
                      {org.name}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>

            {selectedOrganizationId && (
              <Dialog open={isCreateModalOpen} onOpenChange={setIsCreateModalOpen}>
                <DialogTrigger asChild>
                  <Button size="sm" className="h-8 gap-2 bg-slate-900 hover:bg-slate-800 text-white rounded-lg">
                    <Plus className="h-3.5 w-3.5" />
                    {t('common:x2.integration', 'Integration')}
                  </Button>
                </DialogTrigger>
                <DialogContent className="sm:max-w-[480px] rounded-2xl border-0 p-0 overflow-hidden">
                  <div className="flex flex-col h-full bg-white">
                    <div className="px-6 py-4 border-b border-slate-100">
                      <DialogTitle className="text-base font-semibold text-slate-900">{t('common:x2.newShopIntegration', 'Neue Shop-Integration')}</DialogTitle>
                    </div>

                    <div className="p-6 space-y-4">
                      <div className="rounded-lg bg-blue-50/50 px-3 py-2 border border-blue-100 flex items-center gap-2">
                        <div className="w-1.5 h-1.5 rounded-full bg-blue-500" />
                        <span className="text-xs text-blue-700">
                          {t('common:x1.orgColon', 'Organisation:')} <strong>{selectedOrganizationName}</strong>
                        </span>
                      </div>

                      <div className="space-y-1.5">
                        <Label htmlFor="platform-type" className="text-xs font-medium text-slate-700">{t('common:x2.platform', 'Plattform')}</Label>
                        <Select
                          value={formData.platformType}
                          onValueChange={(value) => setFormData(prev => ({ ...prev, platformType: value }))}
                        >
                          <SelectTrigger className="h-9 rounded-lg border-slate-200 text-sm focus:ring-slate-200">
                            <SelectValue />
                          </SelectTrigger>
                          <SelectContent>
                            <SelectItem value="shopify">Shopify</SelectItem>
                            <SelectItem value="woocommerce">WooCommerce</SelectItem>
                            <SelectItem value="jtl">JTL Shop</SelectItem>
                            <SelectItem value="ikas">İkas</SelectItem>
                            <SelectItem value="magento">Magento</SelectItem>
                          </SelectContent>
                        </Select>
                      </div>

                      <div className="space-y-1.5">
                        <Label htmlFor="platform-name" className="text-xs font-medium text-slate-700">{t('common:x1.integrationName', 'Name der Integration')}</Label>
                        <Input
                          id="platform-name"
                          value={formData.platformName}
                          onChange={(e) => setFormData(prev => ({ ...prev, platformName: e.target.value }))}
                          placeholder={t('common:x1.egPremiumShop', 'z.B. Premium Heizungen Shop')}
                          className="h-9 rounded-lg border-slate-200 text-sm focus-visible:ring-slate-200"
                        />
                      </div>

                      {formData.platformType === 'shopify' && (
                        <>
                          <div className="space-y-1.5">
                            <Label htmlFor="shop-url" className="text-xs font-medium text-slate-700">Shop URL</Label>
                            <div className="relative">
                              <Globe className="absolute left-3 top-2.5 h-4 w-4 text-slate-400" />
                              <Input
                                id="shop-url"
                                value={formData.connectionConfig.shop_url}
                                onChange={(e) => setFormData(prev => ({
                                  ...prev,
                                  connectionConfig: { ...prev.connectionConfig, shop_url: e.target.value }
                                }))}
                                placeholder="premium-heizungen.myshopify.com"
                                className="h-9 pl-9 rounded-lg border-slate-200 text-sm focus-visible:ring-slate-200"
                              />
                            </div>
                          </div>

                          <div className="space-y-1.5">
                            <Label htmlFor="access-token" className="text-xs font-medium text-slate-700">Access Token</Label>
                            <div className="relative">
                              <Key className="absolute left-3 top-2.5 h-4 w-4 text-slate-400" />
                              <Input
                                id="access-token"
                                type="password"
                                value={formData.connectionConfig.access_token}
                                onChange={(e) => setFormData(prev => ({
                                  ...prev,
                                  connectionConfig: { ...prev.connectionConfig, access_token: e.target.value }
                                }))}
                                placeholder="shpat_..."
                                className="h-9 pl-9 rounded-lg border-slate-200 text-sm focus-visible:ring-slate-200"
                              />
                            </div>
                          </div>

                          <div className="space-y-1.5">
                            <Label htmlFor="api-version" className="text-xs font-medium text-slate-700">API Version</Label>
                            <Input
                              id="api-version"
                              value={formData.connectionConfig.api_version}
                              onChange={(e) => setFormData(prev => ({
                                ...prev,
                                connectionConfig: { ...prev.connectionConfig, api_version: e.target.value }
                              }))}
                              placeholder="2023-10"
                              className="h-9 rounded-lg border-slate-200 text-sm focus-visible:ring-slate-200"
                            />
                          </div>
                        </>
                      )}

                      {formData.platformType === 'jtl' && (
                        <>
                          <div className="space-y-1.5">
                            <Label htmlFor="jtl-url" className="text-xs font-medium text-slate-700">JTL URL</Label>
                            <div className="relative">
                              <Globe className="absolute left-3 top-2.5 h-4 w-4 text-slate-400" />
                              <Input
                                id="jtl-url"
                                value={formData.connectionConfig.jtl_url}
                                onChange={(e) => setFormData(prev => ({
                                  ...prev,
                                  connectionConfig: { ...prev.connectionConfig, jtl_url: e.target.value }
                                }))}
                                placeholder="https://ihr-shop.jtl-shop.de"
                                className="h-9 pl-9 rounded-lg border-slate-200 text-sm focus-visible:ring-slate-200"
                              />
                            </div>
                          </div>

                          <div className="space-y-1.5">
                            <Label htmlFor="jtl-api-key" className="text-xs font-medium text-slate-700">API Key</Label>
                            <div className="relative">
                              <Key className="absolute left-3 top-2.5 h-4 w-4 text-slate-400" />
                              <Input
                                id="jtl-api-key"
                                type="password"
                                value={formData.connectionConfig.api_key}
                                onChange={(e) => setFormData(prev => ({
                                  ...prev,
                                  connectionConfig: { ...prev.connectionConfig, api_key: e.target.value }
                                }))}
                                placeholder={t('common:x3.yourJtlApiKey', 'Ihr JTL API Key')}
                                className="h-9 pl-9 rounded-lg border-slate-200 text-sm focus-visible:ring-slate-200"
                              />
                            </div>
                          </div>
                        </>
                      )}
                    </div>

                    <DialogFooter className="p-4 border-t border-slate-100 bg-slate-50/50">
                      <Button 
                        variant="outline" 
                        onClick={() => setIsCreateModalOpen(false)} 
                        className="h-9 rounded-lg border-slate-200 text-slate-600 hover:text-slate-900 hover:bg-white"
                      >
                        {t('common:action.cancel', 'Abbrechen')}
                      </Button>
                      <Button
                        onClick={handleCreateIntegration}
                        disabled={
                          !formData.platformName ||
                          createMutation.isPending ||
                          (formData.platformType === 'shopify' && (!formData.connectionConfig.shop_url || !formData.connectionConfig.access_token)) ||
                          (formData.platformType === 'jtl' && (!formData.connectionConfig.jtl_url || !formData.connectionConfig.api_key))
                        }
                        className="h-9 rounded-lg bg-slate-900 text-white hover:bg-slate-800 disabled:opacity-50"
                      >
                        {createMutation.isPending ? 'Erstellt...' : 'Erstellen'}
                      </Button>
                    </DialogFooter>
                  </div>
                </DialogContent>
              </Dialog>
            )}
          </div>
        </CardHeader>
        <CardContent className="p-0">
          {(isLoadingOrganizations || isLoadingAllIntegrations) ? (
            <div className="p-6 space-y-4">
              {[1, 2, 3].map((i) => (
                <div key={i} className="flex items-center gap-4">
                  <Skeleton className="h-10 w-10 rounded-lg" />
                  <div className="space-y-2 flex-1">
                    <Skeleton className="h-4 w-[200px]" />
                    <Skeleton className="h-3 w-[150px]" />
                  </div>
                </div>
              ))}
            </div>
          ) : allIntegrations.length === 0 ? (
            <div className="flex flex-col items-center justify-center gap-3 px-6 py-16 text-center text-slate-500">
              <ShoppingBag className="h-8 w-8 text-slate-300" />
              <h3 className="text-sm font-medium text-slate-600">
                {t('common:x3.noIntegrations', 'Noch keine Integrationen')}
              </h3>
              <p className="text-xs text-slate-400 max-w-xs mx-auto">
                {t('common:x3.chooseOrgConnectShop', 'Wählen Sie eine Organisation und verbinden Sie Ihren ersten Shop.')}
              </p>
            </div>
          ) : (
            <div className="divide-y divide-slate-100">
              <div className="grid grid-cols-12 gap-4 px-6 py-3 bg-slate-50/50 text-xs font-medium text-slate-500 border-b border-slate-100">
                <div className="col-span-5">{t('common:x3.namePlatform', 'Name & Plattform')}</div>
                <div className="col-span-3">{t('common:field.status', 'Status')}</div>
                <div className="col-span-3">{t('common:x1.statistics', 'Statistiken')}</div>
                <div className="col-span-1 text-right">{t('common:field.actions', 'Aktionen')}</div>
              </div>
              
              {(selectedOrganizationId
                ? allIntegrations.filter(integration => integration.organization_id === selectedOrganizationId)
                : allIntegrations
              ).map((integration) => (
                <div key={integration.id} className="group grid grid-cols-12 gap-4 px-6 py-4 items-center hover:bg-slate-50/50 transition-colors">
                  <div className="col-span-5 flex items-center gap-3">
                    <div className="w-10 h-10 rounded-lg bg-slate-50 flex items-center justify-center border border-slate-200">
                      <ShoppingBag className="h-5 w-5 text-slate-400" />
                    </div>
                    <div>
                      <div className="flex items-center gap-2">
                        <h4 className="font-semibold text-sm text-slate-900 truncate">{integration.platform_name}</h4>
                        <Badge variant="outline" className="rounded-md border-slate-200 bg-white px-1.5 py-0 text-[10px] font-medium text-slate-500 capitalize">
                          {integration.platform_type}
                        </Badge>
                      </div>
                      <span className="text-xs text-slate-500 block mt-0.5">
                        {integration.organizationName}
                      </span>
                    </div>
                  </div>

                  <div className="col-span-3">
                    <div className="flex flex-col gap-1.5 items-start">
                      <Badge className={cn(
                        "rounded-full px-2 py-0.5 text-[10px] font-medium border-0",
                        integration.is_active
                          ? "bg-emerald-50 text-emerald-700 ring-1 ring-inset ring-emerald-600/20"
                          : "bg-slate-100 text-slate-600 ring-1 ring-inset ring-slate-500/10"
                      )}>
                        {integration.is_active ? 'Aktiv' : 'Inaktiv'}
                      </Badge>
                      {integration.is_default && (
                        <span className="text-[10px] text-purple-600 font-medium flex items-center gap-1">
                          <div className="w-1 h-1 rounded-full bg-purple-500" />
                          {t('common:x1.standard', 'Standard')}
                        </span>
                      )}
                    </div>
                  </div>

                  <div className="col-span-3">
                    {integration.total_requests > 0 ? (
                      <div className="flex flex-col gap-1">
                        <div className="flex items-center gap-2 text-[10px] text-slate-500">
                          <span className="font-medium text-slate-700">{integration.total_requests}</span>
                          <span>{t('common:x3.totalRequests', 'Anfragen gesamt')}</span>
                        </div>
                        <div className="flex items-center gap-2 h-1 w-24 bg-slate-100 rounded-full overflow-hidden">
                          <div 
                            className="h-full bg-emerald-500" 
                            style={{ width: `${(integration.successful_requests / integration.total_requests) * 100}%` }}
                          />
                        </div>
                      </div>
                    ) : (
                      <span className="text-xs text-slate-400 italic">{t('common:state.noData', 'Keine Daten')}</span>
                    )}
                  </div>
                  
                  <div className="col-span-1 flex justify-end gap-1 opacity-0 group-hover:opacity-100 transition-opacity">
                    <Button
                      variant="ghost"
                      size="icon"
                      onClick={() => handleEditIntegration(integration)}
                      className="h-8 w-8 rounded-lg text-slate-400 hover:text-slate-900 hover:bg-white"
                    >
                      <Edit className="h-4 w-4" />
                    </Button>
                    <Button
                      variant="ghost"
                      size="icon"
                      onClick={() => handleDeleteIntegration(integration.id)}
                      className="h-8 w-8 rounded-lg text-slate-400 hover:text-red-600 hover:bg-red-50"
                    >
                      <Trash2 className="h-4 w-4" />
                    </Button>
                  </div>
                </div>
              ))}
            </div>
          )}
        </CardContent>
      </Card>

      {/* Edit Integration Modal */}
      {editingIntegration && (
        <Dialog open={isEditModalOpen} onOpenChange={setIsEditModalOpen}>
          <DialogContent className="sm:max-w-[480px] rounded-2xl border-0 p-0 overflow-hidden">
            <div className="flex flex-col h-full bg-white">
              <div className="px-6 py-4 border-b border-slate-100">
                <DialogTitle className="text-base font-semibold text-slate-900">{t('common:x3.editIntegration', 'Integration bearbeiten')}</DialogTitle>
              </div>

              <div className="p-6 space-y-4">
                <div className="rounded-lg bg-blue-50/50 px-3 py-2 border border-blue-100 flex items-center gap-2">
                  <div className="w-1.5 h-1.5 rounded-full bg-blue-500" />
                  <span className="text-xs text-blue-700">
                    {t('common:x3.platformColon', 'Platform:')} <strong className="capitalize">{editingIntegration.platform_type}</strong>
                  </span>
                </div>

                <div className="space-y-1.5">
                  <Label htmlFor="edit-platform-name" className="text-xs font-medium text-slate-700">{t('common:x1.integrationName', 'Name der Integration')}</Label>
                  <Input
                    id="edit-platform-name"
                    value={formData.platformName}
                    onChange={(e) => setFormData(prev => ({ ...prev, platformName: e.target.value }))}
                    placeholder={t('common:x1.egPremiumShop', 'z.B. Premium Heizungen Shop')}
                    className="h-9 rounded-lg border-slate-200 text-sm focus-visible:ring-slate-200"
                  />
                </div>

                {formData.platformType === 'shopify' && (
                  <>
                    <div className="space-y-1.5">
                      <Label htmlFor="edit-shop-url" className="text-xs font-medium text-slate-700">Shop URL</Label>
                      <div className="relative">
                        <Globe className="absolute left-3 top-2.5 h-4 w-4 text-slate-400" />
                        <Input
                          id="edit-shop-url"
                          value={formData.connectionConfig.shop_url}
                          onChange={(e) => setFormData(prev => ({
                            ...prev,
                            connectionConfig: { ...prev.connectionConfig, shop_url: e.target.value }
                          }))}
                          placeholder="premium-heizungen.myshopify.com"
                          className="h-9 pl-9 rounded-lg border-slate-200 text-sm focus-visible:ring-slate-200"
                        />
                      </div>
                    </div>

                    <div className="space-y-1.5">
                      <Label htmlFor="edit-access-token" className="text-xs font-medium text-slate-700">Access Token</Label>
                      <div className="relative">
                        <Key className="absolute left-3 top-2.5 h-4 w-4 text-slate-400" />
                        <Input
                          id="edit-access-token"
                          type="password"
                          value={formData.connectionConfig.access_token === '[HIDDEN]' ? '' : formData.connectionConfig.access_token}
                          onChange={(e) => setFormData(prev => ({
                            ...prev,
                            connectionConfig: { ...prev.connectionConfig, access_token: e.target.value }
                          }))}
                          placeholder={formData.connectionConfig.access_token === '[HIDDEN]' ? 'Token verborgen - neuen Token eingeben' : 'shpat_...'}
                          className="h-9 pl-9 rounded-lg border-slate-200 text-sm focus-visible:ring-slate-200"
                        />
                      </div>
                      {formData.connectionConfig.access_token === '[HIDDEN]' && (
                        <p className="text-[10px] text-slate-500 mt-1">
                          Aktueller Token ist verborgen. Wenn Sie es leer lassen, bleibt es unverändert.
                        </p>
                      )}
                    </div>

                    <div className="space-y-1.5">
                      <Label htmlFor="edit-api-version" className="text-xs font-medium text-slate-700">API Version</Label>
                      <Input
                        id="edit-api-version"
                        value={formData.connectionConfig.api_version}
                        onChange={(e) => setFormData(prev => ({
                          ...prev,
                          connectionConfig: { ...prev.connectionConfig, api_version: e.target.value }
                        }))}
                        placeholder="2023-10"
                        className="h-9 rounded-lg border-slate-200 text-sm focus-visible:ring-slate-200"
                      />
                    </div>
                  </>
                )}

                {formData.platformType === 'jtl' && (
                  <>
                    <div className="space-y-1.5">
                      <Label htmlFor="edit-jtl-url" className="text-xs font-medium text-slate-700">JTL URL</Label>
                      <div className="relative">
                        <Globe className="absolute left-3 top-2.5 h-4 w-4 text-slate-400" />
                        <Input
                          id="edit-jtl-url"
                          value={formData.connectionConfig.jtl_url}
                          onChange={(e) => setFormData(prev => ({
                            ...prev,
                            connectionConfig: { ...prev.connectionConfig, jtl_url: e.target.value }
                          }))}
                          placeholder="https://ihr-shop.jtl-shop.de"
                          className="h-9 pl-9 rounded-lg border-slate-200 text-sm focus-visible:ring-slate-200"
                        />
                      </div>
                    </div>

                    <div className="space-y-1.5">
                      <Label htmlFor="edit-jtl-api-key" className="text-xs font-medium text-slate-700">API Key</Label>
                      <div className="relative">
                        <Key className="absolute left-3 top-2.5 h-4 w-4 text-slate-400" />
                        <Input
                          id="edit-jtl-api-key"
                          type="password"
                          value={formData.connectionConfig.api_key === '[HIDDEN]' ? '' : formData.connectionConfig.api_key}
                          onChange={(e) => setFormData(prev => ({
                            ...prev,
                            connectionConfig: { ...prev.connectionConfig, api_key: e.target.value }
                          }))}
                          placeholder={formData.connectionConfig.api_key === '[HIDDEN]' ? 'API Key verborgen - neuen Key eingeben' : 'Ihr JTL API Key'}
                          className="h-9 pl-9 rounded-lg border-slate-200 text-sm focus-visible:ring-slate-200"
                        />
                      </div>
                      {formData.connectionConfig.api_key === '[HIDDEN]' && (
                        <p className="text-[10px] text-slate-500 mt-1">
                          Aktueller API Key ist verborgen. Wenn Sie es leer lassen, bleibt es unverändert.
                        </p>
                      )}
                    </div>
                  </>
                )}
              </div>

              <DialogFooter className="p-4 border-t border-slate-100 bg-slate-50/50">
                <Button 
                  variant="outline" 
                  onClick={() => {
                    setIsEditModalOpen(false);
                    setEditingIntegration(null);
                  }} 
                  className="h-9 rounded-lg border-slate-200 text-slate-600 hover:text-slate-900 hover:bg-white"
                >
                  {t('common:action.cancel', 'Abbrechen')}
                </Button>
                <Button
                  onClick={handleUpdateIntegration}
                  disabled={!formData.platformName || updateMutation.isPending}
                  className="h-9 rounded-lg bg-slate-900 text-white hover:bg-slate-800 disabled:opacity-50"
                >
                  {updateMutation.isPending ? 'Aktualisiert...' : 'Aktualisieren'}
                </Button>
              </DialogFooter>
            </div>
          </DialogContent>
        </Dialog>
      )}
    </div>
  );
};

export default ShopIntegrationsSection;
